<template>
  <div class="booking-view-container">
    <div class="backdrop-gradient"></div>
    
    <!-- Main Card Container -->
    <v-card 
      class="booking-main-card" 
      elevation="24"
      @touchstart="handleTouchStart" 
      @touchend="handleTouchEnd"
    >
      <!-- Header Section -->
      <div class="booking-header">
        <div class="header-content">
          <div class="logo-section">
            <div class="logo-wrapper">
              <img :src="logo" class="header-logo" alt="Logo" />
            </div>
            <h1 class="header-title">Booking</h1>
          </div>
          
          <v-btn 
            class="refresh-btn" 
            icon 
            size="large"
            @click="refreshPage"
          >
            <v-icon class="refresh-icon">mdi-reload</v-icon>
          </v-btn>
        </div>

        <!-- Tab Navigation -->
        <div class="tabs-container">
          <div class="custom-tabs">
            <button
              v-for="(tab, index) in tabsConfig"
              :key="index"
              :class="[
                'custom-tab',
                { 'active': tabs === index }
              ]"
              @click="tabs = index"
            >
              <v-icon class="tab-icon">{{ tab.icon }}</v-icon>
              <span class="tab-text">{{ $t(tab.text) }}</span>
              <div class="tab-indicator" :class="{ 'active': tabs === index }"></div>
            </button>
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div class="content-wrapper">
        <div class="content-container">
          <transition 
            name="slide" 
            mode="out-in"
            @enter="onEnter"
            @leave="onLeave"
          >
            <div v-if="tabs === 0" key="book-now" class="tab-content">
              <BookingPoisList />
            </div>
            <div v-else-if="tabs === 1" key="bookings" class="tab-content">
              <UserBookings />
            </div>
          </transition>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { ref, defineComponent, watch, onMounted, computed } from 'vue'
import UserBookings from '@/components/general/UserBookings.vue'
import BookingPoisList from '@/components/general/BookingPoisList.vue'
import { getToolbarLogoImage, getBottomBarColor } from '@/assets/Branding/branding.js'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'BookingView',
  components: {
    BookingPoisList,
    UserBookings,
  },
  setup() {
    const tabs = ref(0)
    const router = useRouter()
    const logo = ref('')
    const tabColor = ref('')
    let startX = 0

    const tabsConfig = computed(() => [
      {
        icon: 'mdi-format-list-bulleted',
        text: 'booking.book-now-tab'
      },
      {
        icon: 'mdi-format-line-spacing', 
        text: 'booking.bookings'
      }
    ])

    const handleTouchStart = e => {
      startX = e.touches[0].clientX
    }

    const handleTouchEnd = e => {
      const endX = e.changedTouches[0].clientX
      const diffX = startX - endX

      if (Math.abs(diffX) > 50) {
        if (diffX > 0) {
          // Swipe Left
          tabs.value = Math.min(tabs.value + 1, 1)
        } else {
          // Swipe Right  
          tabs.value = Math.max(tabs.value - 1, 0)
        }
      }
    }

    const onEnter = (el) => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(20px)'
      
      setTimeout(() => {
        el.style.transition = 'all 0.4s cubic-bezier(0.23, 1, 0.320, 1)'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
      }, 10)
    }

    const onLeave = (el) => {
      el.style.transition = 'all 0.3s cubic-bezier(0.755, 0.05, 0.855, 0.06)'
      el.style.opacity = '0'
      el.style.transform = 'translateY(-20px)'
    }

    watch(tabs, newValue => {
      if (newValue === 0) {
        router.push({ name: 'BookingPoisList' })
      } else {
        router.push({ name: 'UserBookings' })
      }
    })

    const initializeComponent = () => {
      if (router.currentRoute.value.name === 'BookingPoisList') {
        tabs.value = 0
      } else if (router.currentRoute.value.name === 'UserBookings') {
        tabs.value = 1
      }
    }

    const refreshPage = () => {
      router.go(0)
    }

    onMounted(async () => {
      tabColor.value = await getBottomBarColor()
      logo.value = await getToolbarLogoImage()
      initializeComponent()
    })

    return {
      tabs,
      logo,
      tabColor,
      tabsConfig,
      handleTouchStart,
      handleTouchEnd,
      refreshPage,
      onEnter,
      onLeave,
    }
  },
})
</script>

<style scoped>
.booking-view-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
}

.backdrop-gradient {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%),
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
  z-index: -1;
}

.booking-main-card {
  margin: 0 auto;
  max-width: 100%;
  min-height: 100vh;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 32px 64px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.booking-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  padding: 0;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(33, 150, 243, 0.1);
}

.booking-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 30% 40%, rgba(33, 150, 243, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 70% 80%, rgba(79, 195, 247, 0.08) 0%, transparent 50%);
  pointer-events: none;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  position: relative;
  z-index: 2;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-wrapper {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2196f3 0%, #4fc3f7 100%);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(33, 150, 243, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.logo-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.3);
}

.header-logo {
  height: 26px;
  width: auto;
  filter: brightness(0) invert(1);
}

.header-title {
  color: #2196f3;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
  letter-spacing: -0.3px;
}

.refresh-btn {
  background: linear-gradient(135deg, #2196f3 0%, #4fc3f7 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(33, 150, 243, 0.2);
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.refresh-btn:hover {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.3);
}

.refresh-icon {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.refresh-btn:hover .refresh-icon {
  transform: rotate(180deg);
}

.tabs-container {
  padding: 0 20px 12px;
  position: relative;
  z-index: 2;
}

.custom-tabs {
  display: flex;
  background: rgba(33, 150, 243, 0.08);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 6px;
  border: 1px solid rgba(33, 150, 243, 0.15);
}

.custom-tab {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 10px;
  background: transparent;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(33, 150, 243, 0.7);
  min-height: 60px;
}

.custom-tab:hover {
  background: rgba(33, 150, 243, 0.1);
  color: rgba(33, 150, 243, 0.9);
}

.custom-tab.active {
  background: linear-gradient(135deg, #2196f3 0%, #4fc3f7 100%);
  color: white;
  box-shadow: 
    0 8px 32px rgba(33, 150, 243, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.tab-icon {
  font-size: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-tab.active .tab-icon {
  transform: scale(1.1);
}

.tab-text {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
}

.tab-indicator {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%) scaleX(0);
  width: 30px;
  height: 3px;
  background: #2196f3;
  border-radius: 2px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-indicator.active {
  transform: translateX(-50%) scaleX(1);
  background: white;
}

.content-wrapper {
  background: linear-gradient(to bottom, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(249, 251, 255, 0.95) 100%
  );
  min-height: calc(100vh - 180px);
}

.content-container {
  position: relative;
  overflow: hidden;
}

.tab-content {
  width: 100%;
  min-height: 500px;
}

/* Slide Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.320, 1);
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Responsive Design - Compact */
@media (max-width: 1200px) {
  .booking-main-card {
    margin: 0;
    border-radius: 0;
  }
}

@media (max-width: 968px) {
  .header-content {
    padding: 10px 18px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .tabs-container {
    padding: 0 18px 10px;
  }
}

@media (max-width: 768px) {
  .header-content {
    padding: 8px 14px;
  }
  
  .header-title {
    font-size: 18px;
  }
  
  .logo-wrapper {
    width: 36px;
    height: 36px;
  }
  
  .header-logo {
    height: 22px;
  }
  
  .tabs-container {
    padding: 0 14px 8px;
  }
  
  .custom-tab {
    padding: 10px 6px;
    min-height: 50px;
  }
  
  .tab-icon {
    font-size: 18px;
  }
  
  .tab-text {
    font-size: 11px;
  }
}

@media (max-width: 450px) {
  .header-content {
    padding: 8px 12px;
    gap: 10px;
  }
  
  .header-title {
    font-size: 16px;
  }
  
  .logo-wrapper {
    width: 32px;
    height: 32px;
  }
  
  .header-logo {
    height: 20px;
  }
  
  .tabs-container {
    padding: 0 12px 8px;
  }
  
  .custom-tab {
    padding: 8px 4px;
    min-height: 45px;
  }
  
  .tab-icon {
    font-size: 16px;
  }
  
  .tab-text {
    font-size: 10px;
  }
}

@media (max-width: 355px) {
  .header-content {
    padding: 6px 8px;
  }
  
  .header-title {
    font-size: 14px;
  }
  
  .logo-wrapper {
    width: 28px;
    height: 28px;
  }
  
  .header-logo {
    height: 18px;
  }
  
  .tabs-container {
    padding: 0 8px 6px;
  }
  
  .custom-tab {
    padding: 6px 3px;
    min-height: 40px;
    gap: 4px;
  }
  
  .tab-icon {
    font-size: 14px;
  }
  
  .tab-text {
    font-size: 9px;
  }
}
</style>
<!-- BookingView Component
This component provides a user interface for booking-related functionalities. It includes a toolbar with a logo, a title, and a refresh button. The toolbar also contains tabs for navigating between different booking views.

The toolbar has a prominent elevation and a white background color. The logo is displayed on the left side, followed by the title "Booking". On the right side, there is a refresh button with a reload icon that allows users to refresh the page.

Below the toolbar, there are two tabs: "Book Now" and "Bookings". The "Book Now" tab displays a list of points of interest (POIs) that users can book. The "Bookings" tab shows the user's current bookings.

Users can switch between tabs by clicking on them or by swiping left or right on touch devices. Swiping left switches to the next tab, while swiping right switches to the previous tab.

The component uses Vue Router to navigate between different views based on the selected tab. When the "Book Now" tab is selected, the router navigates to the "BookingPoisList" view. When the "Bookings" tab is selected, the router navigates to the "UserBookings" view.

The component also initializes with a specific tab based on the current route. If the route is "BookingPoisList", the "Book Now" tab is selected. If the route is "UserBookings", the "Bookings" tab is selected.

Visual feedback is provided through the active tab's background color and text color. The active tab has a blue background color and white text color, while the inactive tabs have a white background color and blue text color.

The component also includes a method to handle hot refresh, which reloads the page when the refresh button is clicked.

Overall, this component enhances the user experience by providing a clean and intuitive interface for booking-related functionalities, with easy navigation and visual feedback. -->
